export type ClearFeedUser = {
  email: string
  hash: string
  name: string
}

type ClearFeedOptions = {
  client_id: string
  config: {
    allow_dragging: boolean
    brand_color: string
    icon_size: 'medium'
    icon_type: 'round'
    position: 'right'
  }
  user?: ClearFeedUser
}

export type ClearFeedApi = {
  (action: 'init', options: ClearFeedOptions): Promise<void> | void
  open?: () => void
}

declare global {
  interface Window {
    ClearFeed?: ClearFeedApi
  }
}

const CLEARFEED_SCRIPT_ID = 'clearfeed-chat-widget'
const CLEARFEED_SCRIPT_URL = 'https://cdn.clearfeed.app/chat-widget.js'

let loadPromise: Promise<ClearFeedApi> | undefined

export const loadClearFeed = () => {
  if (window.ClearFeed) {
    return Promise.resolve(window.ClearFeed)
  }

  if (loadPromise) {
    return loadPromise
  }

  loadPromise = new Promise<ClearFeedApi>((resolve, reject) => {
    const removeScript = () => document.getElementById(CLEARFEED_SCRIPT_ID)?.remove()
    const handleLoad = () => {
      if (!window.ClearFeed) {
        removeScript()
        reject(new Error('ClearFeed loaded without exposing its API.'))
        return
      }

      resolve(window.ClearFeed)
    }
    const handleError = () => {
      removeScript()
      reject(new Error('Failed to load ClearFeed.'))
    }
    const existingScript = document.getElementById(CLEARFEED_SCRIPT_ID)

    if (existingScript) {
      existingScript.addEventListener('load', handleLoad, { once: true })
      existingScript.addEventListener('error', handleError, { once: true })
      return
    }

    const script = document.createElement('script')
    script.async = true
    script.id = CLEARFEED_SCRIPT_ID
    script.src = CLEARFEED_SCRIPT_URL
    script.addEventListener('load', handleLoad, { once: true })
    script.addEventListener('error', handleError, { once: true })
    document.body.appendChild(script)
  }).catch((error) => {
    loadPromise = undefined
    throw error
  })

  return loadPromise
}

export const initializeLoadedClearFeed = async (clearFeed: ClearFeedApi, clientId: string, user?: ClearFeedUser) => {
  await clearFeed('init', {
    client_id: clientId,
    config: {
      allow_dragging: true,
      brand_color: '#2f80ac',
      icon_size: 'medium',
      icon_type: 'round',
      position: 'right',
    },
    user,
  })
}

export const resetClearFeedSession = () => {
  // ClearFeed has no logout/reset API, so reload to discard its in-memory Firebase session after app logout clears localStorage.
  window.location.reload()
}
